import {
    Box,
    Grid,
    IconButton,
    SelectChangeEvent,
    Theme,
    Typography
} from "@mui/material";
import MFSelectField from "../../../lib/formik/SelectField";
import { MFTextField } from "../../../lib/formik";
import { TextDatePicker } from "../../onboarding/DatePickerWithTextFeild";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";
import dayjs from "../../../utils/dayjs";
import {
    FormikContextType,
    useFormikContext
} from '../../../lib/formik/Formik';
import {
    CounterPartyBank,
    DematDetailsObj,
    GetDematDetailsResponseBody,
    GetOrderEntryBanksListResponseBody, GetSettlementDetailsResponseBody
} from "../../../redux-store/types/orderManagement";
import FormikSearchDropdown from "../../../lib/formik/FormikSearchDropdown";
import { BANK_SUB_ACCOUNT_TYPES, CLIENT_OFFLINE_TRANSFER_DESCRIPTION, INITIATE_TRANSFER_DESCRIPTION, MPOWER_ACCOUNT_TYPE_MASTER, REFERENCE_NUMBER_DESCRIPTION } from "../../../utils/constant";
import * as yup from 'yup';
import { Dispatch, SetStateAction, useRef } from "react";
import { getDematDetails } from "../../../redux-store/actions/orderManagement";
import { showError } from "../../../redux-store/actions/auth";
import { isModuleAllowed } from "../../../utils/utilityFunctions";
import { CustomTooltip } from "./commonComponents";
import { InfoIconOutlined } from "../../../assets/SvgIcons";

const DEPOSITORY_NAME_LIST = [
    {
        key: "CDSL",
        value: "CDSL"
    }, {
        key: "NSDL",
        value: "NSDL"
    }
]

export const SETTLEMENT_BANK_SOURCE_LIST = [
    {
        key: "Security",
        value: "security"
    },
    {
        key: "Counter Party",
        value: "counterParty"
    }
]

export default function SettlementDetailsForm({
    fetchSettlementDetails,
    settlementDetailsList,
    activeProductType,
    setValidations,
    bankAccountList,
    isLoading,
    setLoadingStatus
}: {
    settlementDetailsList: GetSettlementDetailsResponseBody
    fetchSettlementDetails: (searchInput: string) => void;
    activeProductType: string;
    setValidations: (values: Record<string, any>) => void;
    bankAccountList: GetOrderEntryBanksListResponseBody;
    isLoading: boolean;
    setLoadingStatus: Dispatch<SetStateAction<"" | "apiLoading" | "formSubmitting">>;
}): JSX.Element {
    const depositoryNameRef = useRef<HTMLElement>(null)

    const dispatch = useDispatch();

    const { businessDate = "" } = useSelector((store: RootStateType) => store.navbarInfo);

    const {
        setFieldValue,
        values,
        resetForm
    }: FormikContextType<Record<string, any>> = useFormikContext();

    const handleSettlementDateChange = (selectedDate: any) => {
        setFieldValue('settlementDate', selectedDate?.isValid() ? selectedDate : "", true)
    }

    const handleSettlementBankNumberChange = async (selectedObj: Record<string, any>) => {
        if (!selectedObj) {
            resetForm({
                values: {
                    ...values,
                    settlementBankNumber: null,
                    settlementBank: "",
                    settlementBankIfsc: ""
                }
            })
        }

        if (["Corpus In/Out"].includes(activeProductType)) {
            resetForm({
                values: {
                    ...values,
                    settlementBankNumber: selectedObj,
                    settlementBank: selectedObj?.bankname ?? "",
                    settlementBankIfsc: selectedObj?.ifscCode ?? ""
                }
            })
            return
        }

        resetForm({
            values: {
                ...values,
                settlementBankNumber: selectedObj,
                settlementBank: selectedObj?.bankName ?? "",
                settlementBankIfsc: selectedObj?.ifscCode ?? ""
            }
        })
    }

    const handleSettlementMethodChange = async (event: SelectChangeEvent<unknown>) => {
        if (event.target.value === "Client Offline Transfer") {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                settlementBankNumber: yup
                    .object()
                    .nullable()
                    .test("cannot-be-same-as-investor-bank-account", 'Settlement Bank Number cannot be same as Investor Bank Account', function (value, context) {
                        const { parent } = context;
                        return `${parent?.investorBankAccount?.bankAccNo ?? ""}` !== `${value?.bankAccNo ?? ""}`
                    })
            }))
        } else {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                settlementBankNumber: yup
                    .object()
                    .nullable()
                    .test(
                        "settlement-bank-number-required",
                        "Settlement Bank Number is required",
                        function (value) {
                            return !!value
                        })
                    .test("cannot-be-same-as-investor-bank-account", 'Settlement Bank Number cannot be same as Investor Bank Account', function (value, context) {
                        const { parent } = context;
                        return `${parent?.investorBankAccount?.bankAccNo ?? ""}` !== `${value?.bankAccNo ?? ""}`
                    })
            }))
        }

        resetForm({
            values: {
                ...values,
                settlementMethod: event.target.value,
            }
        })
    }

    const handleSettlementBankSourceChange = (event: SelectChangeEvent<unknown>) => {
        const { security, counterParty } = values

        let currentSettlementBankNumber = null
        let currentSettlementBank = ""
        let currentSettlementBankIfsc = ""

        if (event.target.value === "security") {
            currentSettlementBankNumber = security
                ? {
                    ...(security ?? {}),
                    bankCode: security?.bankCode ?? "",
                    bankAccCcy: security?.bankAccCcy ?? "",
                    bankAccType: security?.bankAccType ?? "",
                    bankAccNo: security?.bankAccNo ?? "",
                    bankAccCode: security?.bankAccCode ?? "",
                    bankName: security?.bankName ?? "",
                    label: `${security?.bankAccNo ?? ""} ${security?.bankName ?? ""}`,
                    value: security?.bankAccNo ?? ""
                }
                : null,
                currentSettlementBank = security?.bankName ?? "",
                currentSettlementBankIfsc = security?.ifscCode ?? ""
        } else if (event.target.value === "counterParty") {
            currentSettlementBankNumber = counterParty
                ? {
                    ...(counterParty?.banks?.[0] ?? {}),
                    label: `${counterParty?.banks?.[0]?.bankAccNo ?? ""} ${counterParty?.banks?.[0]?.bankName ?? ""}`,
                    value: counterParty?.banks?.[0]?.bankAccNo ?? ""
                }
                : null,
                currentSettlementBank = counterParty?.banks?.[0]?.bankName ?? "",
                currentSettlementBankIfsc = counterParty?.banks?.[0]?.ifscCode ?? ""
        }

        resetForm({
            values: {
                ...values,
                settlementBankSource: event.target.value,
                settlementBankNumber: currentSettlementBankNumber,
                settlementBank: currentSettlementBank,
                settlementBankIfsc: currentSettlementBankIfsc,
            }
        })
    }

    const handleDepositoryNameChange = async (event: SelectChangeEvent<unknown>) => {
        try {
            setLoadingStatus("apiLoading")

            setFieldValue("settlementDetailsDepositoryName", event.target.value)

            const payload = {
                portfolioId: values?.account ?? "",
                clientCode: values?.clientName?.clientCode ?? "",
                depositoryName: event.target.value as string
            }

            const getDematDetailsResponse = await (dispatch(getDematDetails(payload)) as unknown as Promise<GetDematDetailsResponseBody>);

            if (!getDematDetailsResponse?.isSuccess) {
                dispatch(showError(getDematDetailsResponse?.message ?? ""))
            }

            resetForm({
                values: {
                    ...values,
                    settlementDetailsDepositoryName: event.target.value,
                    settlementDetailsDpNameList: getDematDetailsResponse?.result ?? [],
                    settlementDetailsDpName: null,
                    settlementDetailsDpId: "",
                    settlementDetailsClientId: ""
                }
            })
        } catch (error) {
            console.log(error ?? "")
        } finally {
            setLoadingStatus("")
            setTimeout(() => {
                setFieldValue("settlementDetailsDepositoryName", event.target.value)
                depositoryNameRef.current?.focus()
            }, 5)
        }
    }

    const handleDpNameChange = (selectedObj: Record<string, any>) => {
        if (!selectedObj) {
            resetForm({
                values: {
                    ...values,
                    settlementDetailsDpName: null,
                    settlementDetailsDpId: "",
                    settlementDetailsClientId: ""
                }
            })

            return
        }

        resetForm({
            values: {
                ...values,
                settlementDetailsDpName: selectedObj as DematDetailsObj,
                settlementDetailsDpId: selectedObj?.depositoryId ?? "",
                settlementDetailsClientId: selectedObj?.clientId ?? ""
            }
        })

    }

    let settlementMethodDropdownOptions = [
        { name: "Online Transfer" },
        { name: "Initiate Transfer" },
        { name: "Client Offline Transfer" }
    ]

    // NON POA/ADVISORY CASE
    if (
        !([
            MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.ADVISORY],
            MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.POA]
        ].includes(MPOWER_ACCOUNT_TYPE_MASTER[(values?.accountType ?? "")?.toUpperCase()]))
    ) {
        settlementMethodDropdownOptions = [
            { name: "Client Offline Transfer" }
        ]
    }
    // POA/ADVISORY CASE
    else if ([
        MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.ADVISORY],
        MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.POA]
    ].includes(MPOWER_ACCOUNT_TYPE_MASTER[(values?.accountType ?? "")?.toUpperCase()])
    ) {
        if (activeProductType === "Corpus In/Out") {
            if (values?.transactionType === "Corpus In") {
                settlementMethodDropdownOptions = [
                    { name: "Client Offline Transfer" }
                ]
            } else if (values?.transactionType === "Corpus Out") {
                settlementMethodDropdownOptions = [
                    { name: "Initiate Transfer" },
                    { name: "Client Offline Transfer" }
                ]
            }
        } else if (activeProductType === "Fee Payment") {
            if (values?.transactionType === "Fee Payment") {
                settlementMethodDropdownOptions = [
                    { name: "Initiate Transfer" }
                ]
            }
        } else if (["Unlisted Equity", "AIF", "PMS", "Margin Money"].includes(activeProductType)) {
            // Sell type
            if (["Sell", "Redemption", "Withdrawal", "Cash In Broker"].includes(values?.transactionType)) {
                settlementMethodDropdownOptions = [
                    // { name: "Online Transfer" },
                    { name: "Client Offline Transfer" }
                ]
            }
            // Buy type
            else {
                settlementMethodDropdownOptions = [
                    { name: "Initiate Transfer" },
                    { name: "Client Offline Transfer" },
                ]
            }
        } else if (["Bonds"].includes(activeProductType)) {
            // NOTE: For Bonds in POA case, only "Client Offline Transfer" is available — same as non-POA.
            // Both POA and non-POA Bonds yield a single option intentionally per product spec.
            settlementMethodDropdownOptions = [
                { name: "Client Offline Transfer" }
            ]
        }
    }

    const settlementDetailsListFromSecurity = [{
        ...(values?.security ?? {}),
        bankCode: values?.security?.bankCode ?? "",
        bankAccCcy: values?.security?.bankAccCcy ?? "",
        bankAccType: values?.security?.bankAccType ?? "",
        bankAccNo: values?.security?.bankAccNo ?? "",
        bankAccCode: values?.security?.bankAccCode ?? "",
        bankName: values?.security?.bankName ?? "",
        label: `${values?.security?.bankAccNo ?? ""} ${values?.security?.bankName ?? ""}`,
        value: values?.security?.bankAccNo ?? ""
    }]

    const settlementDetailsListFromBrokerBankDetails = [{
        ...(values?.brokerBankDetails ?? {}),
        label: `${values?.brokerBankDetails?.bankAccNo ?? ""} ${values?.brokerBankDetails?.bankName ?? ""}`,
        value: values?.brokerBankDetails?.bankAccNo ?? ""
    }]

    const settlementDetailsListFromCounterParty = (values?.counterParty?.banks ?? [])?.map((obj: CounterPartyBank) => ({
        ...obj,
        label: `${obj?.bankAccNo ?? ""} ${obj?.bankName ?? ""}`,
        value: obj?.bankAccNo ?? ""
    }))

    let settlementBankNumberOptionsList: Array<Record<string, any>> = settlementDetailsList?.settlementBanks ?? []

    if (["Unlisted Equity", "Bonds"].includes(activeProductType)) {
        if (values?.settlementBankSource === "security") {
            settlementBankNumberOptionsList = settlementDetailsListFromSecurity ?? []
        } else if (values?.settlementBankSource === "counterParty") {
            settlementBankNumberOptionsList = settlementDetailsListFromCounterParty
        }
    }
    if (["AIF", "PMS"].includes(activeProductType)) {
        settlementBankNumberOptionsList = settlementDetailsListFromSecurity ?? []
    }
    if (["Margin Money"].includes(activeProductType)) {
        settlementBankNumberOptionsList = settlementDetailsListFromBrokerBankDetails ?? []
    }
    if (["Corpus In/Out"].includes(activeProductType)) {
        settlementBankNumberOptionsList = (bankAccountList?.bankDetails ?? [])
    }
    if (["Fee Payment"].includes(activeProductType)) {
        settlementBankNumberOptionsList = values?.advisoryBankDetails ?? []
    }

    return (
        <Grid
            container
            columnSpacing={3}
        >
            <Grid item xs={12} lg={4}>
                <MFSelectField
                    name="settlementMethod"
                    label="Settlement Method *"
                    items={settlementMethodDropdownOptions.map((obj) => ({
                        key: obj.name,
                        value: obj.name,
                    }))}
                    disabled={isLoading || !values?.accountType || !values?.transactionType}
                    onChange={handleSettlementMethodChange}
                />
                <Box
                    sx={{
                        display: 'flex',
                        gap: '5px',
                        alignItems: 'center',
                        marginTop: "-5px"
                    }}
                >
                    <CustomTooltip
                        titleContent={
                            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "10px" }}>
                                <Typography component="span" variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary, fontWeight: "bold" })}>
                                    Initiate Transfer:{" "}
                                    <Typography component="span" variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary, fontWeight: "500" })}>{INITIATE_TRANSFER_DESCRIPTION}</Typography>
                                </Typography>
                                <Typography component="span" variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary, fontWeight: "bold" })}>
                                    Client Offline Transfer:{" "}
                                    <Typography component="span" variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary, fontWeight: "500" })}>{CLIENT_OFFLINE_TRANSFER_DESCRIPTION}</Typography>
                                </Typography>
                            </Box>
                        }
                        tooltipTriggerEle={
                            <Box sx={{ display: "flex", columnGap: "5px" }}>
                                <IconButton sx={{ padding: "0px" }}>
                                    <InfoIconOutlined />
                                </IconButton>
                                <Typography variant="overline_400"
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.text.secondary
                                    })}
                                >
                                    Settlement Methods Info
                                </Typography>
                            </Box>
                        }
                    />
                </Box>
            </Grid>
            <Grid item xs={12} lg={4}>
                <TextDatePicker
                    name="settlementDate"
                    label="Settlement Date *"
                    placeholder=""
                    maxDate={new Date("2099-12-31")}
                    minDate={
                        businessDate ?
                            dayjs(businessDate, "DD/MM/YYYY").toDate() :
                            new Date()
                    }
                    customHandleChange={handleSettlementDateChange}
                    disabled={isLoading}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="referenceNumber"
                    label="Cash Settlement Reference Number"
                    placeholder=""
                    disabled={true}
                    value={values?.referenceNumber ?? ""}
                    endAdornment={
                        <CustomTooltip
                            titleContent={
                                <Typography variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                    {REFERENCE_NUMBER_DESCRIPTION}
                                </Typography>
                            }
                        />
                    }
                />
            </Grid>
            {["unlisted equity", "bonds"].includes(`${activeProductType ?? ""}`.toLowerCase()) && (
                <Grid item xs={12} lg={4}>
                    <MFSelectField
                        name="settlementBankSource"
                        label="Settlement Bank Source"
                        items={SETTLEMENT_BANK_SOURCE_LIST}
                        disabled={isLoading || !values?.counterParty}
                        value={values?.settlementBankSource ?? ""}
                        onChange={handleSettlementBankSourceChange}
                    />
                </Grid>
            )}
            <Grid item xs={12} lg={4}>
                <FormikSearchDropdown
                    name="settlementBankNumber"
                    label={`Settlement Bank Number ${values?.settlementMethod === "Client Offline Transfer" ? "" : "*"}`}
                    options={(settlementBankNumberOptionsList ?? [])
                        ?.filter((obj: Record<string, any>) => obj?.bankAccNo)
                        ?.map((obj: Record<string, any>) => ({
                            ...obj,
                            label: `${obj?.bankAccNo ?? ""} ${obj?.bankName ?? ""} ${obj?.bankname ?? ""} ${BANK_SUB_ACCOUNT_TYPES[`${obj?.subAccountType ?? ""}`.toUpperCase()] ? "(" + BANK_SUB_ACCOUNT_TYPES[`${obj?.subAccountType ?? ""}`.toUpperCase()] + ")" : ""}`,
                            value: obj?.bankAccNo ?? ""
                        })) ?? []}
                    disabled={
                        isLoading ||
                        ((["AIF", "PMS"].includes(activeProductType)) ? !values?.security : false) ||
                        (["Unlisted Equity", "Bonds"].includes(activeProductType) ? (!values?.security && !values?.counterParty) : false) ||
                        (["Margin Money"].includes(activeProductType) ? !values?.broker : false) ||
                        (["Corpus In/Out", "Fee Payment"].includes(activeProductType) ? !values?.investorBankAccount : false)
                    }
                    onChange={handleSettlementBankNumberChange}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="settlementBank"
                    label="Settlement Bank"
                    placeholder=""
                    disabled={true}
                    value={values?.settlementBank ?? ""}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="settlementBankIfsc"
                    label="Settlement Bank IFSC Code"
                    placeholder=""
                    disabled={true}
                    value={values?.settlementBankIfsc ?? ""}
                />
            </Grid>
            {["Unlisted Equity", "AIF", "Bonds"].includes(activeProductType) && (
                <>
                    {/* NOTE: All four fields below (Depository Name, DP Name, DP ID, Client ID) are marked required (*).
                        Their yup validation rules (required) are enforced in baseValues.ts under each product's schema. */}
                    <Grid item xs={12} lg={4}>
                        <MFSelectField
                            inputRef={depositoryNameRef}
                            name="settlementDetailsDepositoryName"
                            label="Depository Name *"
                            items={DEPOSITORY_NAME_LIST}
                            disabled={isLoading || !values.clientName || !values.account}
                            onChange={handleDepositoryNameChange}
                        />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <FormikSearchDropdown
                            name="settlementDetailsDpName"
                            label="DP Name *"
                            options={(values?.settlementDetailsDpNameList ?? [])?.map((obj: DematDetailsObj) => ({
                                ...obj,
                                label: obj?.dpName ?? "",
                                value: obj?.dpName ?? "",
                            })) ?? []}
                            onChange={handleDpNameChange}
                            disabled={isLoading || !values.settlementDetailsDepositoryName}
                        />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <MFTextField
                            name="settlementDetailsDpId"
                            label="DP ID *"
                            placeholder=""
                            disabled={isLoading || !values.settlementDetailsDpName}
                            value={values?.settlementDetailsDpId ?? ""}
                        />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <MFTextField
                            name="settlementDetailsClientId"
                            label="Client ID *"
                            placeholder=""
                            disabled={isLoading || !values.settlementDetailsDpName}
                            value={values?.settlementDetailsClientId ?? ""}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    )
}